// =============================================================================
// Origin UI — src/pages/admin/EvaluationAdminTab.tsx
// Admin Config → Evaluations tab: 4 sub-tabs.
//   1. Cycles — create/edit/open/close/reopen evaluation cycles
//   2. Template — edit sections + questions per cycle
//   3. Rating Scales — CRUD rating scales and their options
//   4. Email Template — edit notification template with preview
// Phase 7, Task 7.7.
// =============================================================================

import React, { useState, useEffect } from 'react';
import { Card, ErrorBanner, LoadingSpinner } from '../../components/common';
import { evaluationApi } from '../../services/evaluationApi';
import { referenceApi } from '../../services/requestApi';
import type {
  EvaluationCycleListItem,
  EvaluationCycleDetail,

  RatingScale,
  TemplateSection,
  TemplateQuestion,
  CycleEmailConfig } from '../../services/evaluationApi';
import styles from './admin.module.css';

type SubTab = 'cycles' | 'template' | 'scales' | 'email';

export function EvaluationAdminTab() {
  const [subTab, setSubTab] = useState<SubTab>('cycles');

  return (
    <div>
      <div className={styles.subTabs}>
        {([
          ['cycles', 'Cycles'],
          ['template', 'Template Editor'],
          ['scales', 'Rating Scales'],
        ] as [SubTab, string][]).map(([key, label]) => (
          <button
            key={key}
            className={`${styles.subTab} ${subTab === key ? styles.subTabActive : ''}`}
            onClick={() => setSubTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'cycles' && <CyclesSubTab />}
      {subTab === 'template' && <TemplateSubTab />}
      {subTab === 'scales' && <RatingScalesSubTab />}
    </div>
  );
}

// =============================================================================
// Sub-Tab 1: Cycles
// =============================================================================

function CyclesSubTab() {
  const [cycles, setCycles] = useState<EvaluationCycleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EvaluationCycleListItem | null>(null);
  const [editingDetail, setEditingDetail] = useState<EvaluationCycleDetail | null>(null);
  const [form, setForm] = useState({
    name: '', cycleType: 'YearEndEvaluation', reviewYear: new Date().getFullYear(),
    employeeDeadline: '', managerDeadline: '', hrFinalDeadline: '',
    excludedDepartments: '', notes: '', cloneFromCycleId: '',
  });
  const [actionError, setActionError] = useState('');
  const [emailModalCycleId, setEmailModalCycleId] = useState<string | null>(null);
  const [emailConfigs, setEmailConfigs] = useState<CycleEmailConfig[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);
  const [emailSaving, setEmailSaving] = useState<string | null>(null);
  const [originalEmailConfigs, setOriginalEmailConfigs] = useState<CycleEmailConfig[]>([]);

  async function openEmailModal(cycleId: string) {
    setEmailModalCycleId(cycleId);
    setEmailLoading(true);
    try {
      const configs = await evaluationApi.getCycleEmails(cycleId);
      setEmailConfigs(configs);
      setOriginalEmailConfigs(JSON.parse(JSON.stringify(configs)));
    } catch (e: any) {
      setActionError(e.message || 'Failed to load email configs');
    } finally {
      setEmailLoading(false);
    }
  }

  function updateLocalConfig(configId: string, field: string, value: any) {
    setEmailConfigs(prev => prev.map(c => c.cycleEmailConfigId === configId ? { ...c, [field]: value } : c));
  }

  async function saveAllEmailConfigs() {
    if (!emailModalCycleId) return;
    setEmailSaving('all');
    try {
      for (const config of emailConfigs) {
        await evaluationApi.updateCycleEmail(emailModalCycleId, config.cycleEmailConfigId, {
          subject: config.subject,
          content: config.content,
          isEnabled: config.isEnabled,
        });
      }
      setOriginalEmailConfigs(JSON.parse(JSON.stringify(emailConfigs)));
      setActionError('');
    } catch (e: any) {
      setActionError(e.message || 'Failed to save');
    } finally {
      setEmailSaving(null);
    }
  }

  function hasUnsavedEmailChanges() {
    return JSON.stringify(emailConfigs) !== JSON.stringify(originalEmailConfigs);
  }

  function closeEmailModal() {
    if (hasUnsavedEmailChanges()) {
      if (!confirm('You have unsaved changes. Close without saving?')) return;
    }
    setEmailModalCycleId(null);
    setEmailConfigs([]);
    setOriginalEmailConfigs([]);
  }

  function toggleEmailEnabled(config: CycleEmailConfig) {
    updateLocalConfig(config.cycleEmailConfigId, 'isEnabled', !config.isEnabled);
  }

  const emailSortOrder: Record<string, number> = {
    CyclePublished: 1,
    ManagerAction: 2,
    ManagerCompleted: 3,
    EmployeeAcknowledged: 4,
    EmployeeSubmitted: 5,
    ManualReminder: 6,
    CycleInReview: 7,
    EvaluationReopened: 8,
    DeadlineApproaching: 9,
    DeadlinePassed: 10,
  };

  const sortedEmailConfigs = [...emailConfigs].sort((a, b) =>
    (emailSortOrder[a.emailType] || 99) - (emailSortOrder[b.emailType] || 99)
  );

  const isLockedOff = (emailType: string) => emailType === 'DeadlineApproaching' || emailType === 'DeadlinePassed';
  const isHidden = (emailType: string) => emailType === 'EmployeeAcknowledged';

  const emailTypeLabel = (t: string) => {
    const labels: Record<string, string> = {
      CyclePublished: 'Cycle Published',
      ManagerAction: 'Manager Action',
      ManualReminder: 'Manual Reminder',
      ManagerCompleted: 'Manager Completed Review',
      EmployeeSubmitted: 'Employee Submitted',
      EmployeeAcknowledged: 'Employee Acknowledged',
      DeadlineApproaching: 'Deadline Approaching',
      DeadlinePassed: 'Deadline Passed',
      CycleInReview: 'Cycle Moved to In Review',
      EvaluationReopened: 'Evaluation Reopened',
    };
    return labels[t] || t;
  };

  const cycleTypeLabel = (t: string) => {
    if (t === 'Both') return 'Both';
    if (t === 'MidYear' || t === 'MidYearReview') return 'Mid-Year';
    if (t === 'YearEnd' || t === 'YearEndEvaluation') return 'Year-End';
    return t;
  };

  // Department picker
  const [departments, setDepartments] = useState<{departmentId: number; name: string}[]>([]);
  const [includedDeptNames, setIncludedDeptNames] = useState<string[]>([]);
  const [showDeptPicker, setShowDeptPicker] = useState(false);
  const [allEmployees, setAllEmployees] = useState<{ employeeId: string; name: string; department: string }[]>([]);
  const [includedEmployeeIds, setIncludedEmployeeIds] = useState<string[]>([]);
  const [showEmpPicker, setShowEmpPicker] = useState(false);
  const [empSearch, setEmpSearch] = useState('');

  // Reopen state
  const [reopeningCycleId, setReopeningCycleId] = useState<string | null>(null);
  const [reopenComment, setReopenComment] = useState('');

  useEffect(() => { load(); loadDepts(); }, []);

  async function loadDepts() {
    try {
      const depts = await referenceApi.departments();
      setDepartments(depts);
        const emps = await referenceApi.employees();
        setAllEmployees(emps.map((e: any) => ({ employeeId: e.employeeId, name: e.fullName || '', department: e.department || '' })));
      setIncludedDeptNames(depts.map((d: any) => d.name));
    } catch { /* departments will be empty */ }
  }

  async function load() {
    try { setLoading(true); setCycles(await evaluationApi.getCycles()); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({
      name: '', cycleType: 'YearEndEvaluation', reviewYear: new Date().getFullYear(),
      employeeDeadline: '', managerDeadline: '', hrFinalDeadline: '',
      excludedDepartments: '', notes: '', cloneFromCycleId: '',
    });
    setIncludedDeptNames(departments.map(d => d.name));
    setShowDeptPicker(false);
    setCreating(true); setEditing(null); setEditingDetail(null); setActionError('');
  }

  async function loadIncludedEmployees(cycleId: string) {
    try {
      const included = await evaluationApi.getIncludedEmployees(cycleId);
      setIncludedEmployeeIds(included.map(e => e.employeeId));
    } catch { setIncludedEmployeeIds([]); }
  }

  async function startEdit(c: EvaluationCycleListItem) {
    try {
      setActionError('');
      const detail = await evaluationApi.getCycle(c.evaluationCycleId);
      const excluded: string[] = detail.excludedDepartments ? (() => { try { return JSON.parse(detail.excludedDepartments); } catch { return []; } })() : [];
      setIncludedDeptNames(departments.filter(d => !excluded.includes(d.name)).map(d => d.name));
      setShowDeptPicker(false);
      setForm({
        name: c.name, cycleType: c.cycleType, reviewYear: c.reviewYear,
        employeeDeadline: c.employeeDeadline?.substring(0, 10) || '',
        managerDeadline: c.managerDeadline?.substring(0, 10) || '',
        hrFinalDeadline: c.hrFinalDeadline?.substring(0, 10) || '',
        excludedDepartments: '', notes: detail.notes || '', cloneFromCycleId: detail.clonedFromCycleId || '',
      });
      setEditingDetail(detail);
      setEditing(c); setCreating(false);
      await loadIncludedEmployees(c.evaluationCycleId);
    } catch (e: any) { setActionError(e.message); }
  }

  async function save() {
    try {
      setActionError('');
      const payload = {
        ...form,
        employeeDeadline: form.employeeDeadline ? new Date(form.employeeDeadline).toISOString() : null,
        managerDeadline: form.managerDeadline ? new Date(form.managerDeadline).toISOString() : null,
        hrFinalDeadline: form.hrFinalDeadline ? new Date(form.hrFinalDeadline).toISOString() : null,
        excludedDepartments: (() => { const ex = departments.map(d => d.name).filter(n => !includedDeptNames.includes(n)); return ex.length > 0 ? JSON.stringify(ex) : null; })(),
        notes: form.notes || null,
        cloneFromCycleId: form.cloneFromCycleId || null,
        isTemplate: false,
        cycleType: form.cycleType,
      };
      if (editing) {
        await evaluationApi.updateCycle(editing.evaluationCycleId, payload);
        await evaluationApi.setIncludedEmployees(editing.evaluationCycleId, includedEmployeeIds);
      } else {
        const created = await evaluationApi.createCycle(payload);
        if (created?.evaluationCycleId) {
          await evaluationApi.setIncludedEmployees(created.evaluationCycleId, includedEmployeeIds);
        }
      }
      setCreating(false); setEditing(null); setEditingDetail(null); setIncludedEmployeeIds([]); setShowEmpPicker(false); setEmpSearch(''); await load();
    } catch (e: any) {
      setActionError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message);
    }
  }

  async function performAction(cycleId: string, action: string) {
    try {
      setActionError('');
      if (action === 'open') await evaluationApi.openCycle(cycleId);
      else if (action === 'inReview') await evaluationApi.moveToInReview(cycleId);
      else if (action === 'close') await evaluationApi.closeCycle(cycleId);
      await load();
    } catch (e: any) {
      setActionError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message);
    }
  }

  async function handleReopen(cycleId: string) {
    if (!reopenComment.trim()) {
      setActionError('A reason is required when reopening a cycle.');
      return;
    }
    try {
      setActionError('');
      await evaluationApi.reopenCycle(cycleId, reopenComment.trim());
      setReopeningCycleId(null); setReopenComment('');
      await load();
    } catch (e: any) {
      setActionError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message);
    }
  }

  function toggleDept(name: string) {
    setIncludedDeptNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  }
  function selectAllDepts() { setIncludedDeptNames(departments.map(d => d.name)); }
  function deselectAllDepts() { setIncludedDeptNames([]); }

  async function handleSendReminders(cycleId: string) {
    if (!confirm('This will send reminder emails to all employees with incomplete evaluations. Continue?')) return;
    try {
      setActionError('');
      const result = await evaluationApi.sendReminders(cycleId);
      setActionError('');
      alert(result.message);
    } catch (e: any) {
      setActionError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message);
    }
  }

  const statusColour = (s: string) => {
    switch (s) {
      case 'Draft': return '#6B7280';
      case 'Open': return '#FF8300';
      case 'InReview': return '#00B0B9';
      case 'Closed': return '#059669';
      default: return '#333';
    }
  };

  const displayStatus = (s: string) => {
    switch (s) {
      case 'Open': return 'Published';
      case 'InReview': return 'In Review';
      default: return s;
    }
  };

  const displayCycleType = (t: string) => {
    switch (t) {
      case 'YearEndEvaluation': return 'Year-End';
      case 'MidYearReview': return 'Mid-Year';
      case 'ProbationReview': return 'Probation';
      default: return t;
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {actionError && <ErrorBanner message={actionError} onDismiss={() => setActionError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{cycles.length} cycles</span>
        <button className={styles.addBtn} onClick={startCreate}>+ New Cycle</button>
      </div>

      {(creating || editing) && (() => {
        const isDraft = creating || editing?.status === 'Draft';
        return (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Cycle' : 'New Cycle'}</h4>
          {!isDraft && <p style={{ fontSize: 12, color: '#9BA5B0', marginBottom: 12 }}>This cycle is {displayStatus(editing?.status || '')}. Only notes can be edited.</p>}
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Name *</label>
              <input className={styles.fieldInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="2026 Mid-Year Review" disabled={!isDraft} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Cycle Type</label>
              <select className={styles.fieldInput} value={form.cycleType} onChange={e => setForm({ ...form, cycleType: e.target.value })} disabled={!isDraft}>
                <option value="YearEndEvaluation">Year-End Evaluation</option>
                <option value="MidYearReview">Mid-Year Review</option>
                <option value="ProbationReview">Probation Review</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Review Year</label>
              <input className={styles.fieldInput} type="number" value={form.reviewYear} onChange={e => setForm({ ...form, reviewYear: parseInt(e.target.value) || 0 })} disabled={!isDraft} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Employee Deadline</label>
              <input className={styles.fieldInput} type="date" value={form.employeeDeadline} onChange={e => setForm({ ...form, employeeDeadline: e.target.value })} disabled={!isDraft} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Manager Deadline</label>
              <input className={styles.fieldInput} type="date" value={form.managerDeadline} onChange={e => setForm({ ...form, managerDeadline: e.target.value })} disabled={!isDraft} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>HR Final Deadline</label>
              <input className={styles.fieldInput} type="date" value={form.hrFinalDeadline} onChange={e => setForm({ ...form, hrFinalDeadline: e.target.value })} disabled={!isDraft} />
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.fieldLabel}>Template</label>
              <select className={styles.fieldInput} value={form.cloneFromCycleId} onChange={e => setForm({ ...form, cloneFromCycleId: e.target.value })} disabled={!isDraft}>
                <option value="">— No template (start blank) —</option>
                {cycles.filter(c => c.isTemplate).map(t => (
                  <option key={t.evaluationCycleId} value={t.evaluationCycleId}>{t.name} ({displayCycleType(t.cycleType)})</option>
                ))}
              </select>
              
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / span 3' }}>
              <label className={styles.fieldLabel}>Departments to Include</label>
              <div
                onClick={() => isDraft && setShowDeptPicker(!showDeptPicker)}
                style={{ cursor: isDraft ? 'pointer' : 'default', border: '1.5px solid #E2E7EC', borderRadius: 10, padding: '10px 14px', fontSize: 14, color: '#0A1E3C', background: isDraft ? '#fff' : '#F8FAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>{departments.length === 0 ? 'Loading departments...' : includedDeptNames.length === departments.length ? `All departments (${departments.length})` : `${includedDeptNames.length} of ${departments.length} departments included`}</span>
                {isDraft && <span style={{ color: '#9BA5B0', fontSize: 12 }}>{showDeptPicker ? '▲' : '▼'}</span>}
              </div>
              {showDeptPicker && (
                <div style={{ border: '1px solid #E2E7EC', borderRadius: 8, padding: 12, marginTop: 4, background: '#fff' }}>
                  {isDraft && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button type="button" className={styles.editBtn} style={{ fontSize: 12, padding: '4px 10px' }} onClick={selectAllDepts}>Select All</button>
                    <button type="button" className={styles.editBtn} style={{ fontSize: 12, padding: '4px 10px' }} onClick={deselectAllDepts}>Deselect All</button>
                  </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {departments.map(d => (
                      <label key={d.departmentId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input type="checkbox" checked={includedDeptNames.includes(d.name)} onChange={() => toggleDept(d.name)} />
                        {d.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.field} style={{ gridColumn: '4 / -1' }}>
              <label className={styles.fieldLabel}>Employees to Include</label>
              <div
                onClick={() => isDraft && setShowEmpPicker(!showEmpPicker)}
                style={{ cursor: isDraft ? 'pointer' : 'default', border: '1.5px solid #E2E7EC', borderRadius: 10, padding: '10px 14px', fontSize: 14, color: '#0A1E3C', background: isDraft ? '#fff' : '#F8FAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>{includedEmployeeIds.length === 0 ? 'No individual employees selected' : `${includedEmployeeIds.length} employee${includedEmployeeIds.length > 1 ? 's' : ''} included`}</span>
                {isDraft && <span style={{ color: '#9BA5B0', fontSize: 12 }}>{showEmpPicker ? '▲' : '▼'}</span>}
              </div>
              {showEmpPicker && isDraft && (
                <div style={{ border: '1px solid #E2E7EC', borderRadius: 8, padding: 12, marginTop: 4, background: '#fff', maxHeight: 250, overflowY: 'auto' }}>
                  <input
                    type="text"
                    value={empSearch}
                    onChange={e => setEmpSearch(e.target.value)}
                    placeholder="Search employees..."
                    style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #E2E7EC', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'DM Sans, sans-serif' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button type="button" className={styles.editBtn} style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setIncludedEmployeeIds([])}>Clear All</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {allEmployees
                      .filter(e => !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) || e.department.toLowerCase().includes(empSearch.toLowerCase()))
                      .map(e => (
                        <label key={e.employeeId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={includedEmployeeIds.includes(e.employeeId)}
                            onChange={() => {
                              setIncludedEmployeeIds(prev =>
                                prev.includes(e.employeeId) ? prev.filter(id => id !== e.employeeId) : [...prev, e.employeeId]
                              );
                            }}
                          />
                          <span>{e.name}</span>
                          <span style={{ fontSize: 11, color: '#9BA5B0' }}>({e.department})</span>
                        </label>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.fieldLabel}>Notes</label>
              <textarea className={styles.fieldInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} style={{ resize: 'vertical', fontFamily: 'inherit' }} placeholder="Internal notes about this cycle..." />
            </div>
          </div>

          {/* Status History (shown when editing, if any history exists) */}
          {editing && editingDetail?.statusHistory && editingDetail.statusHistory.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h5 style={{ fontSize: 13, fontWeight: 700, color: '#0A2240', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #E2E7EC' }}>Status History</h5>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>From</th>
                    <th>To</th>
                    <th>Changed By</th>
                    <th>Date</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {editingDetail.statusHistory.map(h => (
                    <tr key={h.evaluationCycleStatusHistoryId}>
                      <td>{displayStatus(h.fromStatus)}</td>
                      <td><span style={{ color: statusColour(h.toStatus), fontWeight: 600 }}>{displayStatus(h.toStatus)}</span></td>
                      <td>{h.changedBy}</td>
                      <td className={styles.muted}>{new Date(h.changedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>{h.comment || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setEditing(null); setEditingDetail(null); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>{isDraft ? 'Save' : 'Save Notes'}</button>
          </div>
        </Card>
        );
      })()}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Year</th>
            <th>Status</th>
            <th>Evaluations</th>
            <th>Deadlines</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cycles.filter(c => !c.isTemplate).map(c => (
            <>
              <tr key={c.evaluationCycleId}>
                <td className={styles.bold}>{c.name}</td>
                <td>{displayCycleType(c.cycleType)}</td>
                <td>{c.reviewYear}</td>
                <td>
                  <span style={{ color: statusColour(c.status), fontWeight: 600 }}>{displayStatus(c.status)}</span>
                </td>
                <td>{c.completedCount}/{c.evaluationCount}</td>
                <td className={styles.muted}>
                  {c.employeeDeadline ? new Date(c.employeeDeadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                  {' / '}
                  {c.managerDeadline ? new Date(c.managerDeadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                </td>
                <td>
                  {c.status === 'Draft' && (
                    <>
                      <button className={styles.editBtn} onClick={() => startEdit(c)}>Edit</button>
                      <button className={styles.editBtn} onClick={() => openEmailModal(c.evaluationCycleId)} style={{ background: '#E0F7F8', color: '#009BA3' }}>✉ Emails</button>
                      <button className={styles.editBtn} onClick={() => performAction(c.evaluationCycleId, 'open')} style={{ background: '#FDE8E7', color: '#C33A30' }}>Publish</button>
                    </>
                  )}
                  {c.status === 'Open' && (
                    <>
                      <button className={styles.editBtn} onClick={() => startEdit(c)}>Edit</button>
                      <button className={styles.editBtn} onClick={() => openEmailModal(c.evaluationCycleId)} style={{ background: '#E0F7F8', color: '#009BA3' }}>✉ Emails</button>
                      <button className={styles.editBtn} onClick={() => handleSendReminders(c.evaluationCycleId)} style={{ background: '#FFF7ED', color: '#FF8300' }}>📧 Send Reminders</button>
                      <button className={styles.editBtn} onClick={() => performAction(c.evaluationCycleId, 'inReview')} style={{ background: '#EDE9FE', color: '#7C3AED' }}>→ In Review</button>
                    </>
                  )}
                  {c.status === 'InReview' && (
                    <>
                      <button className={styles.editBtn} onClick={() => startEdit(c)}>Edit</button>
                      <button className={styles.editBtn} onClick={() => openEmailModal(c.evaluationCycleId)} style={{ background: '#E0F7F8', color: '#009BA3' }}>✉ Emails</button>
                      <button className={styles.editBtn} onClick={() => handleSendReminders(c.evaluationCycleId)} style={{ background: '#FFF7ED', color: '#FF8300' }}>📧 Send Reminders</button>
                      <button className={styles.editBtn} onClick={() => performAction(c.evaluationCycleId, 'close')} style={{ background: '#E6F7F0', color: '#00AA6E' }}>Close</button>
                    </>
                  )}
                  {c.status === 'Closed' && (
                    <>
                      <button className={styles.editBtn} onClick={() => startEdit(c)}>Edit</button>
                      <button className={styles.editBtn} onClick={() => { setReopeningCycleId(c.evaluationCycleId); setReopenComment(''); setActionError(''); }} style={{ background: '#FFF3E0', color: '#FF8300' }}>Reopen</button>
                    </>
                  )}
                </td>
              </tr>
              {/* Inline reopen comment form */}
              {reopeningCycleId === c.evaluationCycleId && (
                <tr key={`reopen-${c.evaluationCycleId}`}>
                  <td colSpan={7} style={{ padding: 0, border: 'none' }}>
                    <div style={{ margin: '4px 0 8px', padding: '12px 16px', background: '#FFF7ED', border: '1px solid #FFE0B2', borderRadius: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#0A2240', display: 'block', marginBottom: 6 }}>Why is this cycle being reopened? *</label>
                      <textarea
                        className={styles.fieldInput}
                        value={reopenComment}
                        onChange={e => setReopenComment(e.target.value)}
                        rows={2}
                        style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }}
                        placeholder="Provide a reason for reopening this cycle..."
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className={styles.cancelBtn} onClick={() => { setReopeningCycleId(null); setReopenComment(''); }}>Cancel</button>
                        <button className={styles.saveBtn} onClick={() => handleReopen(c.evaluationCycleId)}>Confirm Reopen</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
      {/* Email Config Modal */}
      {emailModalCycleId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '95%', maxWidth: 1500, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 30px rgba(10,34,64,0.15)' }}>
            <div style={{ padding: '20px 28px', borderBottom: '1px solid #E2E7EC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0A2240' }}>Email Configuration</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => saveAllEmailConfigs()} disabled={emailSaving === 'all'}
                  style={{ background: '#FF8300', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {emailSaving === 'all' ? 'Saving...' : 'Save All'}
                </button>
              <button onClick={() => closeEmailModal()} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7885' }}>&times;</button>
              </div>
            </div>
            <div style={{ padding: '16px 28px' }}>
              <p style={{ fontSize: 12, color: '#6B7885', marginBottom: 12 }}>Available placeholders: <code style={{ background: '#F1F4F6', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{'{FullName} {PreferredName} {FirstName} {LastName} {CycleName} {CycleType} {EmployeeDeadline} {ManagerDeadline} {ReviewYear}'}</code></p>
              {emailLoading ? <p>Loading...</p> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E7EC' }}>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0', width: 50 }}>On/Off</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0' }}>Email Type</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0' }}>Subject</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0', width: 280 }}>Content</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0' }}>Trigger</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0' }}>Recipients</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9BA5B0', width: 55 }}>Cycle</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEmailConfigs.filter(c => !isHidden(c.emailType)).map(c => (
                      <React.Fragment key={c.cycleEmailConfigId}>
                      <tr style={{ borderBottom: '1px solid #F1F4F6', opacity: c.isEnabled ? 1 : 0.5 }}>
                        <td style={{ padding: '10px 8px' }}>
                          <button onClick={() => !isLockedOff(c.emailType) && toggleEmailEnabled(c)} 
                            style={{ background: isLockedOff(c.emailType) ? '#9BA5B0' : c.isEnabled ? '#00AA6E' : '#CDD4DB', color: '#fff', border: 'none', borderRadius: 12, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: isLockedOff(c.emailType) ? 'not-allowed' : 'pointer', minWidth: 36 }}
                            title={isLockedOff(c.emailType) ? 'Automated emails — coming soon' : ''}>
                            {isLockedOff(c.emailType) ? 'OFF' : c.isEnabled ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td style={{ padding: '10px 8px', fontWeight: 600, color: '#0A2240', fontSize: 12, whiteSpace: 'nowrap' }}>{emailTypeLabel(c.emailType)}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <textarea value={c.subject} onChange={e => updateLocalConfig(c.cycleEmailConfigId, 'subject', e.target.value)}
                            rows={2} style={{ width: '100%', minWidth: 280, padding: '6px 8px', border: '1.5px solid #E2E7EC', borderRadius: 6, fontSize: 12, fontFamily: 'DM Sans, sans-serif', resize: 'none' }} />
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, color: '#6B7885', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.content.substring(0, 60)}{c.content.length > 60 ? '...' : ''}</span>
                            <button onClick={() => setExpandedEmailId(expandedEmailId === c.cycleEmailConfigId ? null : c.cycleEmailConfigId)}
                              style={{ background: '#FF8300', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {expandedEmailId === c.cycleEmailConfigId ? 'Close' : 'Edit'}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 11, color: '#6B7885' }}>{c.triggerDescription}</td>
                        <td style={{ padding: '10px 8px', fontSize: 11, color: '#6B7885' }}>{c.recipientsDescription}</td>
                        <td style={{ padding: '10px 8px', fontSize: 11, color: '#6B7885' }}>{cycleTypeLabel(c.applicableCycleType)}</td>

                      </tr>
                      {expandedEmailId === c.cycleEmailConfigId && (
                        <tr>
                          <td colSpan={7} style={{ padding: '0 8px 16px', background: '#F8FAFB', borderBottom: '2px solid #FF8300' }}>
                            <div style={{ padding: '12px 16px', background: '#fff', borderRadius: 8, border: '1.5px solid #E2E7EC', marginTop: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: '#0A2240' }}>Edit Content — {emailTypeLabel(c.emailType)}</span>
                                <span style={{ fontSize: 11, color: '#9BA5B0' }}>Use placeholders: {'{PreferredName} {FullName} {FirstName} {LastName} {CycleName} {CycleType} {EmployeeDeadline} {ManagerDeadline} {ReviewYear}'}</span>
                              </div>
                              <textarea value={c.content} onChange={e => updateLocalConfig(c.cycleEmailConfigId, 'content', e.target.value)}
                                rows={12} style={{ width: '100%', padding: '12px', border: '1.5px solid #E2E7EC', borderRadius: 8, fontSize: 13, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6, resize: 'vertical' }} />
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-Tab 2: Template Editor
// =============================================================================

function TemplateSubTab() {
  const [cycles, setCycles] = useState<EvaluationCycleListItem[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [cycle, setCycle] = useState<EvaluationCycleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ratingScales, setRatingScales] = useState<RatingScale[]>([]);

  // Section form
  const [editingSection, setEditingSection] = useState<TemplateSection | null>(null);
  const [creatingSect, setCreatingSect] = useState(false);
  const [sectForm, setSectForm] = useState({ title: '', description: '', respondent: 'Employee', sortOrder: 0 });

  // Question form
  const [editingSectionForQ, setEditingSectionForQ] = useState<string>('');
  const [editingQuestion, setEditingQuestion] = useState<TemplateQuestion | null>(null);
  const [creatingQ, setCreatingQ] = useState(false);
  const [qForm, setQForm] = useState({ title: '', description: '', questionType: 'Rating', ratingScaleId: '', isRequired: true, hasCommentField: true, isOverallRating: false });

  // Overall Rating tooltip
  const [showOverallTip, setShowOverallTip] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cycleList, scales] = await Promise.all([evaluationApi.getCycles(), evaluationApi.getRatingScales()]);
        setCycles(cycleList);
        setRatingScales(scales);
        const templates = cycleList.filter((c: any) => c.isTemplate);
        if (templates.length > 0) setSelectedCycleId(templates[0].evaluationCycleId);
      } catch (e: any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    (async () => {
      try { setCycle(await evaluationApi.getCycle(selectedCycleId)); }
      catch (e: any) { setError(e.message); }
    })();
  }, [selectedCycleId]);

  const isLocked = cycle?.isTemplateLocked || false;

  async function reload() {
    setCycle(await evaluationApi.getCycle(selectedCycleId));
  }

  // ─── Section CRUD ───
  function startCreateSection() {
    const nextOrder = (cycle?.templateSections?.length || 0) + 1;
    setSectForm({ title: '', description: '', respondent: 'Employee', sortOrder: nextOrder });
    setCreatingSect(true); setEditingSection(null);
  }
  function startEditSection(s: TemplateSection) {
    setSectForm({ title: s.title, description: s.description || '', respondent: s.respondent, sortOrder: s.sortOrder });
    setEditingSection(s); setCreatingSect(false);
  }
  async function saveSection() {
    try {
      setError('');
      if (editingSection) {
        await evaluationApi.updateSection(selectedCycleId, editingSection.templateSectionId, sectForm);
      } else {
        await evaluationApi.addSection(selectedCycleId, sectForm);
      }
      setCreatingSect(false); setEditingSection(null); await reload();
    } catch (e: any) { setError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message); }
  }
  async function moveSection(sections: any[], index: number, direction: 'up' | 'down') {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= sections.length) return;
    // Don't allow moving above a system section
    if (direction === 'up' && sections[target].isSystem) return;
    const ordered = [...sections];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    try {
      await evaluationApi.reorderSections(selectedCycleId, ordered.map(s => s.templateSectionId));
      await reload();
    } catch (e: any) { setError(e.message); }
  }

  async function deleteSection(sectionId: string) {
    if (!confirm('Delete this section and all its questions?')) return;
    try { await evaluationApi.deleteSection(selectedCycleId, sectionId); await reload(); }
    catch (e: any) { setError(e.message); }
  }

  // ─── Question CRUD ───
  function startCreateQuestion(sectionId: string) {
    setQForm({ title: '', description: '', questionType: 'Rating', ratingScaleId: ratingScales[0]?.ratingScaleId || '', isRequired: true, hasCommentField: true, isOverallRating: false });
    setEditingSectionForQ(sectionId); setCreatingQ(true); setEditingQuestion(null);
  }
  function startEditQuestion(sectionId: string, q: TemplateQuestion) {
    setQForm({ title: q.title, description: q.description || '', questionType: q.questionType, ratingScaleId: q.ratingScaleId || '', isRequired: q.isRequired, hasCommentField: q.hasCommentField, isOverallRating: q.isOverallRating });
    setEditingSectionForQ(sectionId); setEditingQuestion(q); setCreatingQ(false);
  }
  async function saveQuestion() {
    try {
      setError('');
      // Validate: only one Overall Rating per template
      if (qForm.isOverallRating) {
        const existingOverall = cycle?.templateSections
          ?.flatMap(s => s.questions)
          .find(q => q.isOverallRating && q.templateQuestionId !== editingQuestion?.templateQuestionId);
        if (existingOverall) {
          setError("You can't have more than one Overall Rating per Template");
          return;
        }
      }
      const payload = { ...qForm, ratingScaleId: qForm.questionType === 'Rating' ? qForm.ratingScaleId : null };
      if (editingQuestion) {
        await evaluationApi.updateQuestion(selectedCycleId, editingQuestion.templateQuestionId, payload);
      } else {
        await evaluationApi.addQuestion(selectedCycleId, editingSectionForQ, payload);
      }
      setCreatingQ(false); setEditingQuestion(null); setEditingSectionForQ(''); await reload();
    } catch (e: any) { setError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message); }
  }
  async function moveQuestion(sectionId: string, questions: any[], index: number, direction: 'up' | 'down') {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= questions.length) return;
    const ordered = [...questions];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    try {
      await evaluationApi.reorderQuestions(selectedCycleId, sectionId, ordered.map(q => q.templateQuestionId));
      await reload();
    } catch (e: any) { setError(e.message); }
  }

  async function deleteQuestion(questionId: string) {
    if (!confirm('Delete this question?')) return;
    try { await evaluationApi.deleteQuestion(selectedCycleId, questionId); await reload(); }
    catch (e: any) { setError(e.message); }
  }

  // Template creation form
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', cycleType: 'MidYearReview', audience: 'Employee' });

  const displayCycleType = (t: string) => {
    switch (t) {
      case 'YearEndEvaluation': return 'Year-End';
      case 'MidYearReview': return 'Mid-Year';
      case 'ProbationReview': return 'Probation';
      default: return t;
    }
  };

  function startCreateTemplate() {
    setTemplateForm({ name: '', cycleType: 'MidYearReview', audience: 'Employee' });
    setCreatingTemplate(true);
  }

  async function saveTemplate() {
    if (!templateForm.name.trim()) { setError('Template name is required.'); return; }
    try {
      setError('');
      const fullName = `${templateForm.audience === 'FrontOffice' ? 'Front Office' : 'Employee'} — ${templateForm.name.trim()}`;
      const created = await evaluationApi.createCycle({
        name: fullName, cycleType: templateForm.cycleType, reviewYear: new Date().getFullYear(),
        employeeDeadline: new Date().toISOString(), managerDeadline: new Date().toISOString(),
        hrFinalDeadline: new Date().toISOString(), isTemplate: true,
      });
      const updatedCycles = await evaluationApi.getCycles();
      setCycles(updatedCycles);
      setSelectedCycleId(created.evaluationCycleId);
      setCycle(created);
      setCreatingTemplate(false);
    } catch (e: any) { setError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message); }
  }

  // Helper: renders the section form fields (shared between create and edit)
  function renderSectionForm(title: string) {
    return (
      <div className={styles.formCard} style={{ margin: '12px 0' }}>
        <h4 className={styles.formTitle}>{title}</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Title *</label>
            <input className={styles.fieldInput} value={sectForm.title} onChange={e => setSectForm({ ...sectForm, title: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Respondent</label>
            <select className={styles.fieldInput} value={sectForm.respondent} onChange={e => setSectForm({ ...sectForm, respondent: e.target.value })}>
              <option value="Employee">Employee</option>
              <option value="Manager">Manager</option>
            </select>
          </div>
          <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <label className={styles.fieldLabel}>Description</label>
            <input className={styles.fieldInput} value={sectForm.description} onChange={e => setSectForm({ ...sectForm, description: e.target.value })} />
          </div>
        </div>
        <div className={styles.formActions}>
          <button className={styles.cancelBtn} onClick={() => { setCreatingSect(false); setEditingSection(null); }}>Cancel</button>
          <button className={styles.saveBtn} onClick={saveSection}>Save Section</button>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label className={styles.fieldLabel} style={{ margin: 0 }}>Template:</label>
          <select className={styles.fieldInput} value={selectedCycleId} onChange={e => setSelectedCycleId(e.target.value)} style={{ minWidth: 280 }}>
            {cycles.filter(c => c.isTemplate).map(c => <option key={c.evaluationCycleId} value={c.evaluationCycleId}>{c.name} ({displayCycleType(c.cycleType)})</option>)}
            {cycles.filter(c => c.isTemplate).length === 0 && <option value="">No templates yet</option>}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isLocked && selectedCycleId && <button className={styles.addBtn} onClick={startCreateSection}>+ Add Section</button>}
          <button className={styles.addBtn} onClick={startCreateTemplate} style={{ background: '#EDE9FE', color: '#7C3AED' }}>+ New Template</button>
        </div>
      </div>

      {/* Inline template creation form */}
      {creatingTemplate && (
        <div className={styles.formCard} style={{ margin: '12px 0' }}>
          <h4 className={styles.formTitle}>New Template</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Template Name *</label>
              <input className={styles.fieldInput} value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="Mid-Year Review 2026" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Cycle Type</label>
              <select className={styles.fieldInput} value={templateForm.cycleType} onChange={e => setTemplateForm({ ...templateForm, cycleType: e.target.value })}>
                <option value="MidYearReview">Mid-Year Review</option>
                <option value="YearEndEvaluation">End of Year Review</option>
                <option value="ProbationReview">Probation Review</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Audience</label>
              <select className={styles.fieldInput} value={templateForm.audience} onChange={e => setTemplateForm({ ...templateForm, audience: e.target.value })}>
                <option value="Employee">Employee (Back Office)</option>
                <option value="FrontOffice">Front Office</option>
              </select>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => setCreatingTemplate(false)}>Cancel</button>
            <button className={styles.saveBtn} onClick={saveTemplate}>Save Template</button>
          </div>
        </div>
      )}

      {/* Section CREATE form stays at the top */}
      {creatingSect && renderSectionForm('New Section')}

      {/* Sections list */}
      {cycle?.templateSections?.map((section, si) => (
        <div key={section.templateSectionId} style={{ marginBottom: 16, background: "#FFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h4 style={{ margin: 0, color: '#0A2240', fontSize: 15 }}>
                {si + 1}. {section.title}
                <span style={{ fontSize: 11, fontWeight: 500, color: '#888', marginLeft: 8, background: '#f0f0f0', padding: '2px 8px', borderRadius: 4 }}>
                  {section.respondent}
                </span>
              </h4>
              {section.description && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{section.description}</p>}
            </div>
            {!isLocked && !section.isSystem && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button className={styles.editBtn} onClick={() => moveSection(cycle?.templateSections || [], si, 'up')} style={{ padding: '2px 6px', fontSize: 11, visibility: si <= 1 || (cycle?.templateSections?.[si - 1]?.isSystem) ? 'hidden' : 'visible' }}>▲</button>
                <button className={styles.editBtn} onClick={() => moveSection(cycle?.templateSections || [], si, 'down')} style={{ padding: '2px 6px', fontSize: 11, visibility: si === (cycle?.templateSections?.length || 0) - 1 ? 'hidden' : 'visible' }}>▼</button>
                <button className={styles.editBtn} onClick={() => startEditSection(section)}>Edit</button>
                <button className={styles.deleteBtn} onClick={() => deleteSection(section.templateSectionId)}>Delete</button>
              </div>
            )}
          </div>

          {/* Section EDIT form renders inline */}
          {editingSection?.templateSectionId === section.templateSectionId && renderSectionForm('Edit Section')}

          {/* Employee Information: read-only system fields */}
          {section.isSystem ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '8px 0' }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Employee Name</span>
                <div style={{ fontSize: 14, color: '#6B7885', marginTop: 4 }}>From employee record</div>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Job Title</span>
                <div style={{ fontSize: 14, color: '#6B7885', marginTop: 4 }}>From employee record</div>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Line Manager</span>
                <div style={{ fontSize: 14, color: '#6B7885', marginTop: 4 }}>From employee record</div>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Review Period</span>
                <div style={{ fontSize: 14, color: '#6B7885', marginTop: 4 }}>From cycle dates</div>
              </div>
            </div>
          ) : (
          <>
          {/* Questions in this section */}
          <table className={styles.table} style={{ marginBottom: 8 }}>
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>Question</th>
                <th>Type</th>
                <th>Scale</th>
                <th>Req</th>
                <th>Overall</th>
                {!isLocked && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {section.questions.map((q, qi) => (
                <tr key={q.templateQuestionId}>
                  <td>{qi + 1}</td>
                  <td className={styles.bold}>{q.title}</td>
                  <td><span className={styles.mono}>{q.questionType}</span></td>
                  <td className={styles.muted}>{q.ratingScaleName || '—'}</td>
                  <td>{q.isRequired ? '✓' : '—'}</td>
                  <td>{q.isOverallRating ? '⭐' : '—'}</td>
                  {!isLocked && (
                    <td>
                      <button className={styles.editBtn} onClick={() => moveQuestion(section.templateSectionId, section.questions, qi, 'up')} style={{ padding: '2px 6px', fontSize: 11, visibility: qi === 0 ? 'hidden' : 'visible' }}>▲</button>
                      <button className={styles.editBtn} onClick={() => moveQuestion(section.templateSectionId, section.questions, qi, 'down')} style={{ padding: '2px 6px', fontSize: 11, visibility: qi === section.questions.length - 1 ? 'hidden' : 'visible' }}>▼</button>
                      <button className={styles.editBtn} onClick={() => startEditQuestion(section.templateSectionId, q)}>Edit</button>
                      <button className={styles.deleteBtn} onClick={() => deleteQuestion(q.templateQuestionId)}>Del</button>
                    </td>
                  )}
                </tr>
              ))}
              {section.questions.length === 0 && (
                <tr><td colSpan={isLocked ? 6 : 7} style={{ color: '#999', fontStyle: 'italic', textAlign: 'center' }}>No questions yet.</td></tr>
              )}
            </tbody>
          </table>

          {!isLocked && (
            <button className={styles.addBtn} style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => startCreateQuestion(section.templateSectionId)}>+ Add Question</button>
          )}

          {/* Question create/edit form (shown inline under its section) */}
          {(creatingQ || editingQuestion) && editingSectionForQ === section.templateSectionId && (
            <div className={styles.formCard} style={{ marginTop: 12 }}>
              <h4 className={styles.formTitle}>{editingQuestion ? 'Edit Question' : 'New Question'}</h4>
              <div className={styles.formGrid}>
                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <label className={styles.fieldLabel}>Question Title *</label>
                  <input className={styles.fieldInput} value={qForm.title} onChange={e => setQForm({ ...qForm, title: e.target.value })} />
                </div>
                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <label className={styles.fieldLabel}>Description</label>
                  <input className={styles.fieldInput} value={qForm.description} onChange={e => setQForm({ ...qForm, description: e.target.value })} />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Type</label>
                  <select className={styles.fieldInput} value={qForm.questionType} onChange={e => setQForm({ ...qForm, questionType: e.target.value })}>
                    <option value="Rating">Rating</option>
                    <option value="FreeText">Free Text</option>
                  </select>
                </div>
                {qForm.questionType === 'Rating' && (
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Rating Scale</label>
                    <select className={styles.fieldInput} value={qForm.ratingScaleId} onChange={e => setQForm({ ...qForm, ratingScaleId: e.target.value })}>
                      {ratingScales.map(s => <option key={s.ratingScaleId} value={s.ratingScaleId}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div className={styles.checkField}>
                  <label><input type="checkbox" checked={qForm.isRequired} onChange={e => setQForm({ ...qForm, isRequired: e.target.checked })} /> Required</label>
                </div>
                <div className={styles.checkField}>
                  <label><input type="checkbox" checked={qForm.hasCommentField} onChange={e => setQForm({ ...qForm, hasCommentField: e.target.checked })} /> Has Comment</label>
                </div>
                <div className={styles.checkField} style={{ position: 'relative' }}>
                  <label>
                    <input type="checkbox" checked={qForm.isOverallRating} onChange={e => setQForm({ ...qForm, isOverallRating: e.target.checked })} /> Overall Rating
                    <span
                      onMouseEnter={() => setShowOverallTip(true)}
                      onMouseLeave={() => setShowOverallTip(false)}
                      style={{ marginLeft: 6, cursor: 'help', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#E0F7F8', color: '#00B0B9', fontSize: 10, fontWeight: 700, flexShrink: 0 }}
                    >i</span>
                  </label>
                  {showOverallTip && (
                    <div style={{
                      position: 'absolute', bottom: '100%', left: 0, marginBottom: 8, width: 320,
                      background: '#FFFFFF', border: '1px solid #E2E7EC', borderRadius: 10,
                      boxShadow: '0 4px 12px rgba(10,34,64,0.08)', padding: '12px 16px',
                      fontSize: 13, color: '#0A1E3C', lineHeight: 1.6, zIndex: 10,
                    }}>
                      Is this question the overall performance rating for the employee in this cycle? Only one question per template should have this checked.
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.formActions}>
                <button className={styles.cancelBtn} onClick={() => { setCreatingQ(false); setEditingQuestion(null); setEditingSectionForQ(''); }}>Cancel</button>
                <button className={styles.saveBtn} onClick={saveQuestion}>Save Question</button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      ))}

      {(!cycle?.templateSections || cycle.templateSections.length === 0) && (
        <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>No template sections. Click "+ Add Section" to build the evaluation template.</p>
      )}
    </div>
  );
}

// =============================================================================
// Sub-Tab 3: Rating Scales
// =============================================================================

function RatingScalesSubTab() {
  const [scales, setScales] = useState<RatingScale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RatingScale | null>(null);
  const [form, setForm] = useState({ name: '', description: '', options: [{ label: '', description: '', numericValue: 1, sortOrder: 1 }] });

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setScales(await evaluationApi.getRatingScales()); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ name: '', description: '', options: [{ label: '', description: '', numericValue: 5, sortOrder: 1 }] });
    setCreating(true); setEditing(null);
  }

  function startEdit(s: RatingScale) {
    setForm({
      name: s.name, description: s.description || '',
      options: s.options.map(o => ({ label: o.label, description: o.description || '', numericValue: o.numericValue, sortOrder: o.sortOrder })),
    });
    setEditing(s); setCreating(false);
  }

  function addOption() {
    const nextVal = Math.max(1, (form.options.length > 0 ? Math.min(...form.options.map(o => o.numericValue)) : 2) - 1);
    setForm({ ...form, options: [...form.options, { label: '', description: '', numericValue: nextVal, sortOrder: 0 }] });
  }

  function moveOption(index: number, direction: 'up' | 'down') {
    const opts = [...form.options];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= opts.length) return;
    [opts[index], opts[target]] = [opts[target], opts[index]];
    setForm({ ...form, options: opts });
  }

  function removeOption(index: number) {
    setForm({ ...form, options: form.options.filter((_, i) => i !== index) });
  }

  function updateOption(index: number, field: string, value: any) {
    const opts = [...form.options];
    (opts[index] as any)[field] = value;
    setForm({ ...form, options: opts });
  }

  async function save() {
    try {
      setError('');
      const payload = {
        ...form,
        options: form.options.map((opt, i) => ({ ...opt, sortOrder: i + 1 })),
      };
      if (editing) {
        await evaluationApi.updateRatingScale(editing.ratingScaleId, payload);
      } else {
        await evaluationApi.createRatingScale(payload);
      }
      setCreating(false); setEditing(null); await load();
    } catch (e: any) { setError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message); }
  }

  async function deleteScale(id: string) {
    if (!confirm('Delete this rating scale?')) return;
    try { await evaluationApi.deleteRatingScale(id); await load(); }
    catch (e: any) { setError(e?.body ? JSON.parse(e.body)?.error || e.message : e.message); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>{scales.length} rating scales</span>
        <button className={styles.addBtn} onClick={startCreate}>+ New Scale</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Rating Scale' : 'New Rating Scale'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Name *</label>
              <input className={styles.fieldInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Standard 5-Point" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Description</label>
              <input className={styles.fieldInput} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <h5 style={{ fontSize: 13, fontWeight: 600, color: '#0A2240', margin: '16px 0 8px' }}>Options</h5>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <span style={{ flex: 2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Label</span>
            <span style={{ flex: 3, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Description</span>
            <span style={{ width: 60, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#9BA5B0' }}>Score</span>
            <span style={{ width: 62 }}></span>
            <span style={{ width: 28 }}></span>
          </div>
          {form.options.map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <input className={styles.fieldInput} style={{ flex: 2 }} value={opt.label} onChange={e => updateOption(i, 'label', e.target.value)} placeholder="Label" />
              <input className={styles.fieldInput} style={{ flex: 3 }} value={opt.description} onChange={e => updateOption(i, 'description', e.target.value)} placeholder="Description (optional)" />
              <input className={styles.fieldInput} style={{ width: 60 }} type="number" value={opt.numericValue} onChange={e => updateOption(i, 'numericValue', parseInt(e.target.value) || 0)} />
              <button className={styles.editBtn} onClick={() => moveOption(i, 'up')} style={{ padding: '2px 6px', fontSize: 11, visibility: i === 0 ? 'hidden' : 'visible' }}>▲</button>
              <button className={styles.editBtn} onClick={() => moveOption(i, 'down')} style={{ padding: '2px 6px', fontSize: 11, visibility: i === form.options.length - 1 ? 'hidden' : 'visible' }}>▼</button>
              <button className={styles.deleteBtn} onClick={() => removeOption(i)} style={{ padding: '4px 8px' }}>×</button>
            </div>
          ))}
          <button className={styles.editBtn} onClick={addOption} style={{ marginTop: 4, fontSize: 12 }}>+ Add Option</button>

          <div className={styles.formActions} style={{ marginTop: 16 }}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setEditing(null); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save Scale</button>
          </div>
        </Card>
      )}

      {scales.map(s => (
        <div key={s.ratingScaleId} style={{ marginBottom: 12, background: "#FFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <span className={styles.bold}>{s.name}</span>
              {s.isSystem && <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>(system)</span>}
              {s.description && <p className={styles.muted} style={{ margin: '2px 0 0' }}>{s.description}</p>}
            </div>
            {!s.isSystem && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button className={styles.editBtn} onClick={() => startEdit(s)}>Edit</button>
                <button className={styles.deleteBtn} onClick={() => deleteScale(s.ratingScaleId)}>Delete</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {s.options.map(o => (
              <span key={o.ratingScaleOptionId} style={{ padding: '3px 10px', background: '#f0f0f0', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                {o.label} ({o.numericValue})
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================

